import { ReactElement, useEffect, useState } from 'react';
import { NextPage } from 'next';
import { AccountInfo, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import { MainLayout } from '@components';
import { NotificationToast } from '@components/specific/notificationToast/NotificationToast';
import {
	AccountContextProvider,
	LegalEntityContextProvider,
	ModalContextProvider,
	NotificationsContextProvider,
	ReactQueryProvider,
	ScreenSizeContextProvider,
	UserIdentityContextProvider,
	UserTypeContextProvider,
} from '@context';
import { getMsalInstance, msalService } from '@services';

import type { AppProps } from 'next/app';

import LoginPage from './login';

import './app.scss';

import ReactGA from 'react-ga4';

import '@fontsource-variable/noto-sans';
// required css from DatePicker library
import '@components/general/dateField/DatePicker.css';
import '@components/general/timeField/TimePicker.css';
import '@components/general/dateTimeField/DateTimePicker.css';
import '@components/general/dateTimeField/Calendar.css';
// required css from React Tooltip library
import 'react-tooltip/dist/react-tooltip.css';

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_MEASUREMENT_ID;

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
GA_MEASUREMENT_ID && ReactGA.initialize(GA_MEASUREMENT_ID);

type AppLayoutProps = AppProps & {
	Component: NextPage & { layout: typeof MainLayout };
	pageProps: unknown;
};

function MyApp({ Component, pageProps }: AppLayoutProps) {
	const msalInstance = getMsalInstance();
	const [showToast, setShowToast] = useState<boolean>(false);

	useEffect(() => {
		const initializeMsal = async () => {
			if (!msalInstance) return;
			
			try {
				// Initialize MSAL instance
				await msalService.initialize();
				
				// Add event callback
				msalInstance.addEventCallback((event: any) => {
					if (event.eventType === EventType.LOGIN_SUCCESS && event?.payload) {
						const account = event.payload as AccountInfo;
						msalInstance.setActiveAccount(account);
						// Do not add routing here as it generates Error: Cancel rendering route
					}
				});

				// Handle redirect promise after initialization
				msalInstance.handleRedirectPromise().catch((err: Error) => {
					const isInteractionRequiredError = err instanceof InteractionRequiredAuthError;
					
					// Log the actual error details for debugging
					console.error('MSAL handleRedirectPromise error:', err);
					console.error('Error name:', err.name);
					console.error('Error message:', err.message);
					console.error('Error stack:', err.stack);
					console.error('Is InteractionRequiredAuthError:', isInteractionRequiredError);
					console.error('Full error object:', JSON.stringify(err, Object.getOwnPropertyNames(err), 2));
					
					setShowToast(!isInteractionRequiredError);
				});
			} catch (error) {
				console.error('MSAL initialization error:', error);
				setShowToast(true);
			}
		};

		initializeMsal();
	}, []); // Run once on mount

	const Layout = Component.layout || ((children: ReactElement) => <>{children}</>);
	
	if (!msalInstance) return null;
	
	return (
		<MsalProvider instance={msalInstance}>
			<AuthenticatedTemplate>
				<ReactQueryProvider>
					<UserIdentityContextProvider>
						<UserTypeContextProvider>
							<AccountContextProvider>
								<LegalEntityContextProvider>
									<NotificationsContextProvider>
										<ScreenSizeContextProvider>
											<ModalContextProvider>
												<Layout>
													{
														<>
															<NotificationToast show={showToast} itemType={'error'} />
															<Component {...pageProps} />
														</>
													}
												</Layout>
											</ModalContextProvider>
										</ScreenSizeContextProvider>
									</NotificationsContextProvider>
								</LegalEntityContextProvider>
							</AccountContextProvider>
						</UserTypeContextProvider>
					</UserIdentityContextProvider>
				</ReactQueryProvider>
			</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				<NotificationToast show={showToast} itemType={'error'} />
				<LoginPage />
			</UnauthenticatedTemplate>
		</MsalProvider>
	);
}

export default MyApp;
